import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { SignedIn, SignedOut, RedirectToSignIn, useUser, useClerk, AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from './lib/api.js';
import { useLang } from './lib/i18n.js';
import { Icon } from './components/Icon.js';
import { Avatar } from './components/ui.js';
import { useMerchant, type Merchant } from './hooks/useMerchant.js';

import { Overview } from './pages/Overview.js';
import { Inbox } from './pages/Inbox.js';
import { ConversationView } from './pages/ConversationView.js';
import { Customers } from './pages/Customers.js';
import { Products } from './pages/Products.js';
import { AddProduct } from './pages/AddProduct.js';
import { ImportProducts } from './pages/ImportProducts.js';
import { Orders } from './pages/Orders.js';
import { Knowledge } from './pages/Knowledge.js';
import { BrandVoice } from './pages/BrandVoice.js';
import { DeliveryZones } from './pages/DeliveryZones.js';
import { TeamPage } from './pages/Team.js';
import { Billing } from './pages/Billing.js';
import { Analytics } from './pages/Analytics.js';
import { Sandbox } from './pages/Sandbox.js';
import { ApiKeys } from './pages/ApiKeys.js';
import { Onboarding } from './pages/Onboarding.js';
import { ConnectMeta } from './pages/ConnectMeta.js';
import { SocialPosts } from './pages/SocialPosts.js';
import { SupportChannels } from './pages/SupportChannels.js';
import { Credits } from './pages/Credits.js';
import { SignInPage, SignUpPage } from './pages/Auth.js';

export function App() {
  return (
    <Routes>
      <Route path="/sign-in/*" element={<><SignedOut><SignInPage /></SignedOut><SignedIn><Navigate to="/" replace /></SignedIn></>} />
      <Route path="/sign-up/*" element={<><SignedOut><SignUpPage /></SignedOut><SignedIn><Navigate to="/" replace /></SignedIn></>} />
      <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback signInForceRedirectUrl="/" signUpForceRedirectUrl="/onboarding" />} />
      <Route path="/onboarding" element={<SignedIn><Onboarding /></SignedIn>} />
      <Route path="/*" element={
        <>
          <SignedIn><Shell /></SignedIn>
          <SignedOut><RedirectToSignIn /></SignedOut>
        </>
      } />
    </Routes>
  );
}

function Shell() {
  const { data: merchant, isLoading } = useMerchant();
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLang();

  if (isLoading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>{t('common.loading')}</div>;
  if (!merchant) return <Navigate to="/onboarding" replace />;

  return (
    <div className="app">
      <Sidebar merchant={merchant} open={menuOpen} onClose={() => setMenuOpen(false)} />
      <main className="main">
        <Topbar onMenu={() => setMenuOpen(true)} />
        <ContentRouter />
      </main>
      <div className={`scrim ${menuOpen ? 'scrim--on' : ''}`} onClick={() => setMenuOpen(false)} />
    </div>
  );
}

function ContentRouter() {
  const loc = useLocation();
  const isInbox = loc.pathname === '/inbox';
  return (
    <div className="content" style={isInbox ? { padding: 0 } : undefined}>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/conversations/:conversationId" element={<ConversationView />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/new" element={<AddProduct />} />
        <Route path="/products/import" element={<ImportProducts />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/brand-voice" element={<BrandVoice />} />
        <Route path="/delivery-zones" element={<DeliveryZones />} />
        <Route path="/channels" element={<ConnectMeta />} />
        <Route path="/channels/meta" element={<ConnectMeta />} />
        <Route path="/social-posts" element={<SocialPosts />} />
        <Route path="/support-channels" element={<SupportChannels />} />
        <Route path="/credits" element={<Credits />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/sandbox" element={<Sandbox />} />
        <Route path="/api-keys" element={<ApiKeys />} />
      </Routes>
    </div>
  );
}

interface NavItem { id: string; labelKey: string; icon: string; path: string; }
interface NavGroup { labelKey?: string; items: NavItem[]; }

function buildNav(): NavGroup[] {
  return [
    { items: [{ id: 'overview', labelKey: 'nav.overview', icon: 'home', path: '/' }] },
    { labelKey: 'nav.group.conversations', items: [
      { id: 'inbox',     labelKey: 'nav.inbox',     icon: 'inbox', path: '/inbox' },
      { id: 'customers', labelKey: 'nav.customers', icon: 'users', path: '/customers' },
    ]},
    { labelKey: 'nav.group.store', items: [
      { id: 'products', labelKey: 'nav.products', icon: 'box',  path: '/products' },
      { id: 'orders',   labelKey: 'nav.orders',   icon: 'cart', path: '/orders' },
    ]},
    { labelKey: 'nav.group.autoreply', items: [
      { id: 'social', labelKey: 'nav.social', icon: 'megaphone', path: '/social-posts' },
    ]},
    { labelKey: 'nav.group.ai', items: [
      { id: 'sandbox',   labelKey: 'nav.sandbox',   icon: 'sparkles', path: '/sandbox' },
      { id: 'knowledge', labelKey: 'nav.knowledge', icon: 'book',     path: '/knowledge' },
      { id: 'voice',     labelKey: 'nav.voice',     icon: 'voice',    path: '/brand-voice' },
      { id: 'zones',     labelKey: 'nav.zones',     icon: 'truck',    path: '/delivery-zones' },
    ]},
    { labelKey: 'nav.group.settings', items: [
      { id: 'channels',  labelKey: 'nav.channels',  icon: 'plug',  path: '/channels' },
      { id: 'whatsapp',  labelKey: 'nav.whatsapp',  icon: 'phone', path: '/support-channels' },
      { id: 'team',      labelKey: 'nav.team',      icon: 'team',  path: '/team' },
      { id: 'credits',   labelKey: 'nav.credits',   icon: 'bolt',  path: '/credits' },
      { id: 'billing',   labelKey: 'nav.billing',   icon: 'card',  path: '/billing' },
      { id: 'analytics', labelKey: 'nav.analytics', icon: 'chart', path: '/analytics' },
    ]},
  ];
}

function Sidebar({ merchant, open, onClose }: { merchant: Merchant; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const { t } = useLang();
  const groups = buildNav();

  const isActive = (path: string) => {
    if (path === '/') return loc.pathname === '/';
    return loc.pathname === path || loc.pathname.startsWith(path + '/');
  };

  return (
    <aside className={`side ${open ? 'side--open' : ''}`}>
      <div className="side__brand">
        <span className="brandDot" />
        <div style={{ minWidth: 0 }}>
          <div className="side__brandName latin">ChatRunner</div>
          <div className="side__brandSub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{merchant.name}</div>
        </div>
      </div>

      <div className="side__scroll">
        {groups.map((group, gi) => (
          <div className="navgroup" key={gi}>
            {group.labelKey && <div className="navgroup__label">{t(group.labelKey)}</div>}
            {group.items.map((it) => (
              <button
                key={it.id}
                className={`navitem ${isActive(it.path) ? 'navitem--active' : ''}`}
                onClick={() => { navigate(it.path); onClose(); }}
              >
                <Icon name={it.icon} />
                <span>{t(it.labelKey)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="side__foot">
        <CreditPill onClick={() => { navigate('/credits'); onClose(); }} />
        <UserMenu />
      </div>
    </aside>
  );
}

function CreditPill({ onClick }: { onClick: () => void }) {
  const api = useApi();
  const { t } = useLang();
  const { data } = useQuery({
    queryKey: ['credit-pill'],
    queryFn: () => api.get<{ total_remaining: number; base_included: number; base_used: number }>('/credits'),
    refetchInterval: 30_000,
    retry: false,
  });
  const total = data?.total_remaining ?? 0;
  const included = data?.base_included ?? 5000;
  const used = data?.base_used ?? 0;
  const pct = included > 0 ? Math.max(0, Math.round(((included - used) / included) * 100)) : 0;
  return (
    <button className="creditpill" onClick={onClick}>
      <div className="creditpill__top">
        <span className="creditpill__label"><Icon name="bolt" />{t('side.creditpill')}</span>
        <span className="creditpill__val">{total.toLocaleString('en-US')}</span>
      </div>
      <div className="creditpill__bar"><div className="creditpill__fill" style={{ width: pct + '%' }} /></div>
    </button>
  );
}

function UserMenu() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { t } = useLang();
  const initial = (user?.firstName?.[0] || user?.fullName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0] || 'U').toUpperCase();
  return (
    <button className="usermenu" onClick={() => signOut({ redirectUrl: '/sign-in' })} title={t('side.signout')}>
      <Avatar initial={initial} size={34} />
      <div style={{ minWidth: 0 }}>
        <div className="usermenu__name">{user?.fullName ?? user?.firstName ?? 'User'}</div>
        <div className="usermenu__mail" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.primaryEmailAddress?.emailAddress}</div>
      </div>
      <span className="usermenu__chev"><Icon name="logout" size={16} /></span>
    </button>
  );
}

// Topbar breadcrumb maps path segment → [titleKey, parentKey].
const CRUMBS: Record<string, [string, string]> = {
  '': ['nav.overview', ''],
  inbox: ['nav.inbox', 'nav.group.conversations'],
  conversations: ['nav.inbox', 'nav.group.conversations'],
  customers: ['nav.customers', 'nav.group.conversations'],
  products: ['nav.products', 'nav.group.store'],
  orders: ['nav.orders', 'nav.group.store'],
  'social-posts': ['nav.social', 'nav.group.autoreply'],
  sandbox: ['nav.sandbox', 'nav.group.ai'],
  knowledge: ['nav.knowledge', 'nav.group.ai'],
  'brand-voice': ['nav.voice', 'nav.group.ai'],
  'delivery-zones': ['nav.zones', 'nav.group.ai'],
  channels: ['nav.channels', 'nav.group.settings'],
  'support-channels': ['nav.whatsapp', 'nav.group.settings'],
  team: ['nav.team', 'nav.group.settings'],
  credits: ['nav.credits', 'nav.group.settings'],
  billing: ['nav.billing', 'nav.group.settings'],
  analytics: ['nav.analytics', 'nav.group.settings'],
};

function Topbar({ onMenu }: { onMenu: () => void }) {
  const loc = useLocation();
  const { lang, setLang, t } = useLang();
  const seg = loc.pathname.split('/')[1] ?? '';
  const [titleKey, parentKey] = CRUMBS[seg] ?? ['nav.overview', ''];
  return (
    <header className="topbar">
      <button className="topbar__menu" onClick={onMenu}><Icon name="dots" /></button>
      <div className="topbar__title">
        {parentKey && <span className="topbar__crumb">{t(parentKey)}</span>}
        <span className="topbar__h">{t(titleKey)}</span>
      </div>
      <div className="topbar__spacer" />
      <div className="search">
        <Icon name="search" />
        <input placeholder={t('topbar.search.ph')} />
      </div>
      <div className="langswitch">
        <button className={lang === 'ar' ? 'on' : ''} onClick={() => setLang('ar')}>ع</button>
        <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
      </div>
      <button className="iconbtn"><Icon name="bell" /><span className="iconbtn__dot" /></button>
    </header>
  );
}
